import { forwardRef, MouseEvent, ForwardedRef, PropsWithoutRef } from 'react';
import { ChevronDown16 } from 'ui/icons/aurora/16px/ChevronDown16';
import { ChevronUp16 } from 'ui/icons/aurora/16px/ChevronUp16';
import { colors } from 'ui/theme/colors';
import { CircleSpinner } from 'ui/spinner/CircleSpinner';
import { ALIGNMENT, BASIC_SELECT_SIZES } from './constants';
import { SelectValueDisplayProps, SelectValueType } from './types';
import { ErrorIcon, IconWrapper, ValueDisplay, ValueWrapper } from './SelectValueDisplay.styles';

const safetyContent = {
  multipleSelectedText: 'options selected',
};

export const SelectValueDisplay = forwardRef(function InnerSelectValue<
  TValue extends SelectValueType = string,
>(
  {
    content = safetyContent,
    isDisabled = false,
    isError = false,
    isOpen = false,
    isNonInteractive = false,
    isReadOnly = false,
    placeholder = '',
    selectedOptions = [],
    toggleDropdown,
    verticalAlignment = ALIGNMENT.DOWN,
    children,
    size = BASIC_SELECT_SIZES.MEDIUM,
    selectValueDisplayText,
    suffixUsed = false,
    isLoading = false,
    ...props
  }: PropsWithoutRef<SelectValueDisplayProps<TValue>>,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  const { badgeText, id, Prefix, Suffix, label } = selectedOptions[0] ?? {};

  const labelOrSuffixValueDisplay = suffixUsed ? Suffix : label;
  const icon = verticalAlignment === ALIGNMENT.DOWN ? <ChevronDown16 /> : <ChevronUp16 />;

  return (
    <ValueWrapper
      aria-disabled={isDisabled}
      aria-expanded={isOpen}
      aria-haspopup="listbox"
      data-test="select-value-display"
      data-values={selectedOptions.map(({ value }) => value)}
      onClick={
        isNonInteractive
          ? undefined
          : (event: MouseEvent) => {
              if (typeof ref !== 'function') {
                ref?.current?.focus(); // force focus for FF/Safari
              }
              event.stopPropagation();
              toggleDropdown();
            }
      }
      ref={ref}
      size={size}
      type="button"
      {...props}
    >
      {selectedOptions.length > 1 ? (
        <ValueDisplay as="div" data-test="selected-options-multiple" role={undefined}>
          {children ||
            selectValueDisplayText ||
            `${selectedOptions.length} ${
              content?.multipleSelectedText || safetyContent.multipleSelectedText
            }`}
        </ValueDisplay>
      ) : (
        // @ts-ignore
        <ValueDisplay
          as="div"
          badgeText={badgeText}
          data-test={`selected-option-${id || 'empty'}`}
          id="selected-option"
          Prefix={Prefix}
          role={undefined}
          size={size}
          Suffix={Suffix}
        >
          {children || labelOrSuffixValueDisplay || placeholder}
        </ValueDisplay>
      )}
      <IconWrapper isReadOnly={isReadOnly}>
        {isError && !isReadOnly && <ErrorIcon />}
        {isLoading ? <CircleSpinner size={16} color={colors.secondaryText} /> : icon}
      </IconWrapper>
    </ValueWrapper>
  );
});
